/**
 * Cuisine/course vocabularies (D26) for the recipe screens: picker groups,
 * labels, URL-param parsing and the per-member rankings helpers. Pure and
 * unit-tested.
 */
import {
  COURSE_LABELS,
  COURSES,
  CUISINE_LABELS,
  CUISINES,
  type Course,
  type Cuisine,
  type RecipeRankingRow,
} from '@fewerbags/schema';

/** At most this many cuisines and courses per recipe (mirrors the API schema). */
export const MAX_PER_KIND = 4;

/** Cuisine picker groups, so 24 chips scan as four short rows. Covers every key once. */
export const CUISINE_GROUPS: ReadonlyArray<{ label: string; keys: readonly Cuisine[] }> = [
  {
    label: 'Americas',
    keys: [
      'american',
      'bbq',
      'southern',
      'cajun',
      'tex-mex',
      'mexican',
      'latin-american',
      'caribbean',
    ],
  },
  {
    label: 'Europe & Mediterranean',
    keys: ['italian', 'french', 'spanish', 'mediterranean', 'greek', 'middle-eastern'],
  },
  {
    label: 'Asia & Pacific',
    keys: ['indian', 'asian', 'chinese', 'japanese', 'korean', 'thai', 'vietnamese', 'hawaiian'],
  },
  { label: 'Other', keys: ['fusion', 'other'] },
];

/** Course picker groups: what fills a dinner plate first, then everything else. */
export const COURSE_GROUPS: ReadonlyArray<{ label: string; keys: readonly Course[] }> = [
  { label: 'Meal', keys: ['main', 'side', 'soup', 'salad', 'bread', 'sauce'] },
  { label: 'Other times', keys: ['appetizer', 'breakfast', 'dessert', 'snack', 'drink'] },
];

export const cuisineLabel = (key: Cuisine): string => CUISINE_LABELS[key];
export const courseLabel = (key: Course): string => COURSE_LABELS[key];

/** Select options sorted by label, with an "any" first entry. */
export const CUISINE_OPTIONS = [
  { value: '', label: 'Any cuisine' },
  ...[...CUISINES]
    .map((key) => ({ value: key, label: CUISINE_LABELS[key] }))
    .sort((a, b) => a.label.localeCompare(b.label)),
];
export const COURSE_OPTIONS = [
  { value: '', label: 'Any course' },
  ...COURSES.map((key) => ({ value: key, label: COURSE_LABELS[key] })),
];

const CUISINE_SET = new Set<string>(CUISINES);
const COURSE_SET = new Set<string>(COURSES);

/** A `?cuisine=` value from the URL, or '' when absent/unknown. */
export function parseCuisineParam(value: string | null): '' | Cuisine {
  return value !== null && CUISINE_SET.has(value) ? (value as Cuisine) : '';
}
export function parseCourseParam(value: string | null): '' | Course {
  return value !== null && COURSE_SET.has(value) ? (value as Course) : '';
}

/**
 * Toggle `key` in a picker selection: removing always works, adding is
 * ignored once `max` are chosen (the UI disables those chips too).
 */
export function toggleChoice<T extends string>(
  selected: readonly T[],
  key: T,
  max = MAX_PER_KIND,
): T[] {
  if (selected.includes(key)) return selected.filter((k) => k !== key);
  if (selected.length >= max) return [...selected];
  return [...selected, key];
}

/** "4.5" / "—" for a household or member average. */
export function formatAvg(avg: number | null): string {
  return avg === null ? '—' : avg.toFixed(1).replace(/\.0$/, '');
}

/** 1-based rank with ties sharing a number ("1, 2, 2, 4"); unrated rows get null. */
export function rankNumbers(
  rows: ReadonlyArray<Pick<RecipeRankingRow, 'householdAvg' | 'stars'>>,
  sortBy: 'household' | string,
  offset = 0,
): Array<number | null> {
  const score = (row: Pick<RecipeRankingRow, 'householdAvg' | 'stars'>) =>
    sortBy === 'household' ? row.householdAvg : (row.stars[sortBy] ?? null);
  const out: Array<number | null> = [];
  let prev: number | null | undefined;
  let prevRank = 0;
  rows.forEach((row, i) => {
    const s = score(row);
    if (s === null) {
      out.push(null);
      return;
    }
    const rank = s === prev ? prevRank : offset + i + 1;
    out.push(rank);
    prev = s;
    prevRank = rank;
  });
  return out;
}
